<template>
  <v-card class="period-selector-card" elevation="2">
    <v-card-text>
      <v-row align="center" justify="space-between">
        <v-col cols="12" md="4">
          <v-select
            v-model="internalYear"
            :items="yearOptions"
            label="Année"
            density="comfortable"
            hide-details
            @update:model-value="onYearChange"
          ></v-select>
        </v-col>

        <v-col cols="12" md="4">
          <v-select
            v-model="internalMonth"
            :items="monthOptions"
            label="Mois (Cumulatif)"
            density="comfortable"
            hide-details
            clearable
            @update:model-value="onMonthChange"
          >
            <template v-slot:selection="{ item }">
              {{ item.title }}
            </template>
          </v-select>
        </v-col>

        <v-col cols="12" md="4" class="d-flex align-center justify-end">
          <div class="d-flex align-center">
            <v-chip
              v-if="isHistorized"
              color="primary"
              size="small"
              :prepend-icon="mdiCalculator"
              class="mr-2"
            >
              {{ calculationMethod }}
            </v-chip>

            <v-chip
              v-if="isHistorized"
              color="success"
              size="small"
              :prepend-icon="mdiCheckCircle"
            >
              Historisé
            </v-chip>
            <v-chip
              v-else
              color="warning"
              size="small"
              :prepend-icon="mdiAlert"
            >
              Temps réel
            </v-chip>
          </div>
        </v-col>
      </v-row>

      <v-row v-if="internalMonth" class="mt-2">
        <v-col>
          <v-alert type="info" density="compact" variant="tonal">
            Les données affichées sont cumulatives de janvier à
            {{ getMonthName(internalMonth) }}
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import { mdiCheckCircle, mdiAlert, mdiCalculator } from "@mdi/js";

export default {
  name: "PeriodSelector",
  props: {
    year: {
      type: Number,
      required: true,
    },
    month: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      mdiCheckCircle,
      mdiAlert,
      mdiCalculator,
      internalYear: this.year,
      internalMonth: this.month,
      monthNames: [
        "Janvier",
        "Février",
        "Mars",
        "Avril",
        "Mai",
        "Juin",
        "Juillet",
        "Août",
        "Septembre",
        "Octobre",
        "Novembre",
        "Décembre",
      ],
    };
  },
  computed: {
    ...mapState({
      historizedPeriods: (state) => state.kpi.historizedPeriods,
      configurationItems: (state) => state.kpi.configurationItems,
    }),
    /**
     * Génère la liste des années disponibles
     */
    yearOptions() {
      const currentYear = new Date().getFullYear();
      const startYear = 2020;
      const years = [];

      for (let year = currentYear; year >= startYear; year--) {
        years.push({
          title: year.toString(),
          value: year,
        });
      }

      return years;
    },
    /**
     * Génère la liste des mois disponibles
     */
    monthOptions() {
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      return this.monthNames.map((name, index) => {
        const monthValue = index + 1;
        const disabled =
          this.internalYear === currentYear && monthValue >= currentMonth;

        return {
          title: name,
          value: monthValue,
          disabled: disabled,
        };
      });
    },
    /**
     * Vérifie si la période sélectionnée est historisée
     */
    isHistorized() {
      if (!this.historizedPeriods || this.historizedPeriods.length === 0) {
        return false;
      }

      return this.historizedPeriods.some((period) => {
        if (this.internalMonth) {
          return (
            period.year === this.internalYear &&
            period.month === this.internalMonth
          );
        } else {
          return period.year === this.internalYear && period.month === null;
        }
      });
    },
    /**
     * Détermine la méthode de calcul utilisée selon la configuration
     */
    calculationMethod() {
      if (!this.configurationItems || this.configurationItems.length === 0) {
        return "Chargement...";
      }

      const useCalculatedCogsConfig = this.configurationItems.find(
        (item) => item.name === "UseCalculatedCogs",
      );
      const manualCogsConfig = this.configurationItems.find(
        (item) => item.name === "ManualCogs",
      );
      const calculatedCogsConfig = this.configurationItems.find(
        (item) => item.name === "CalculatedCogs",
      );

      if (!useCalculatedCogsConfig) {
        return "Calculated YTD N";
      }

      // Si UseCalculatedCogs est "true", on utilise CalculatedCogs (YTD N)
      // Sinon, on utilise ManualCogs (N-1)
      if (useCalculatedCogsConfig.value === "true") {
        const value = calculatedCogsConfig ? calculatedCogsConfig.value : "N/A";
        return `Calculated YTD N : ${value}%`;
      } else {
        const value = manualCogsConfig ? manualCogsConfig.value : "N/A";
        return `Manuel (N-1) : ${value}%`;
      }
    },
  },
  watch: {
    year(newVal) {
      this.internalYear = newVal;
      // Recharger la configuration si historisé
      if (this.isHistorized) {
        this.loadConfiguration();
      }
    },
    month(newVal) {
      this.internalMonth = newVal;
      // Recharger la configuration si historisé
      if (this.isHistorized) {
        this.loadConfiguration();
      }
    },
    isHistorized(newVal) {
      // Charger la configuration uniquement si la période est historisée
      if (newVal) {
        this.loadConfiguration();
      }
    },
  },
  mounted() {
    // Charger la configuration si la période initiale est historisée
    if (this.isHistorized) {
      this.loadConfiguration();
    }
  },
  methods: {
    /**
     * Charge la configuration avec les paramètres year et month
     */
    loadConfiguration() {
      const params = {
        year: this.internalYear,
        month: this.internalMonth,
      };
      this.$store.dispatch("kpi/fetchConfiguration", params);
    },
    /**
     * Gère le changement d'année
     */
    onYearChange() {
      this.$emit("update:year", this.internalYear);
      this.$emit("period-changed", {
        year: this.internalYear,
        month: this.internalMonth,
      });
    },
    /**
     * Gère le changement de mois
     */
    onMonthChange() {
      this.$emit("update:month", this.internalMonth);
      this.$emit("period-changed", {
        year: this.internalYear,
        month: this.internalMonth,
      });
    },
    /**
     * Retourne le nom du mois pour un numéro donné
     */
    getMonthName(monthNumber) {
      if (monthNumber && monthNumber >= 1 && monthNumber <= 12) {
        return this.monthNames[monthNumber - 1];
      }
      return "";
    },
  },
};
</script>

<style scoped>
.period-selector-card {
  margin-bottom: 20px;
}
</style>
